import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaList, FaChartPie, FaHome, FaTh, FaBox, FaTags, FaSignOutAlt, FaChevronRight, FaBars, FaImage, FaTimes } from "react-icons/fa";
import { AuthContext } from "../components/AuthContext";
import { API_URL } from "../constant";

const Categories = () => {
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState({ id: null, name: "", image: "" });
  const [originalCategory, setOriginalCategory] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [showAddCard, setShowAddCard] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);

  // Fetch categories from the API.
  const refreshCategories = useCallback(async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };
      const response = await fetch(`${API_URL}/api/categories`, { headers });
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
      navigate("/login");
      return;
    }
    refreshCategories();
  }, [authToken, navigate, refreshCategories]);

  // Handle input change for category name.
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle image selection.
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Remove selected image.
  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // When editing, show the current image as a preview if no new image is selected.
  useEffect(() => {
    if (editingId !== null && formData.image && !selectedImage) {
      setImagePreview(`${API_URL}/storage/${formData.image}`);
    }
  }, [editingId, formData.image, selectedImage]);

  // Handle adding a new category.
  const handleAdd = () => {
    const { name } = formData;
    if (!name || !selectedImage) {
      setModalMessage("Name and Image are required.");
      setModalType("error");
      setShowModal(true);
      return;
    }
    const fd = new FormData();
    fd.append("category_name", name);
    fd.append("image", selectedImage);

    fetch(`${API_URL}/api/categories`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: fd,
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(text || "Failed to add category");
          });
        }
        return response.json();
      })
      .then(() => {
        refreshCategories();
        setFormData({ id: null, name: "", image: "" });
        setSelectedImage(null);
        setImagePreview(null);
        setShowAddCard(false);
        setModalMessage("Category added successfully!");
        setModalType("success");
        setShowModal(true);
      })
      .catch((error) => {
        console.error("Error adding category:", error);
        setModalMessage("Error adding category: " + error.message);
        setModalType("error");
        setShowModal(true);
      });
  };

  // Handle editing a category.
  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      id: category.id,
      name: category.category_name,
      image: category.image,
    });
    setOriginalCategory(category);
    setSelectedImage(null);
    setImagePreview("");
    setShowAddCard(true);
  };

  // Handle updating a category.
  const handleUpdate = () => {
    if (!originalCategory) {
      setModalMessage("Original category data not available.");
      setModalType("error");
      setShowModal(true);
      return;
    }

    const imageChanged = selectedImage !== null;
    const nameChanged = formData.name !== originalCategory.category_name;

    if (!nameChanged && !imageChanged) {
      setModalMessage("No changes made.");
      setModalType("info");
      setShowModal(true);
      return;
    }

    if (imageChanged) {
      const fd = new FormData();
      if (nameChanged) {
        fd.append("category_name", formData.name);
      }
      fd.append("image", selectedImage);

      fetch(`${API_URL}/api/categories/${formData.id}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: fd,
      })
        .then((response) => {
          if (!response.ok) {
            return response.text().then((text) => {
              throw new Error(text || "Failed to update category");
            });
          }
          return response.json();
        })
        .then(() => {
          refreshCategories();
          setEditingId(null);
          setOriginalCategory(null);
          setFormData({ id: null, name: "", image: "" });
          setSelectedImage(null);
          setImagePreview(null);
          setShowAddCard(false);
          setModalMessage("Category updated successfully!");
          setModalType("success");
          setShowModal(true);
        })
        .catch((error) => {
          console.error("Error updating category:", error);
          setModalMessage("Error updating category: " + error.message);
          setModalType("error");
          setShowModal(true);
        });
    } else if (nameChanged && !imageChanged) {
      const payload = { category_name: formData.name };
      fetch(`${API_URL}/api/categories/${formData.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      })
        .then((response) => {
          if (!response.ok) {
            return response.text().then((text) => {
              throw new Error(text || "Failed to update category");
            });
          }
          return response.json();
        })
        .then(() => {
          refreshCategories();
          setEditingId(null);
          setOriginalCategory(null);
          setFormData({ id: null, name: "", image: "" });
          setSelectedImage(null);
          setImagePreview(null);
          setShowAddCard(false);
          setModalMessage("Category updated successfully!");
          setModalType("success");
          setShowModal(true);
        })
        .catch((error) => {
          console.error("Error updating category:", error);
          setModalMessage("Error updating category: " + error.message);
          setModalType("error");
          setShowModal(true);
        });
    }
  };

  // Handle delete.
  const handleDelete = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  // Confirm deletion of a category.
  const confirmDeleteCategory = () => {
    fetch(`${API_URL}/api/categories/${categoryToDelete.id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(text || "Error deleting category");
          });
        }
        refreshCategories();
        setShowDeleteModal(false);
        setModalMessage("Category deleted successfully!");
        setModalType("success");
        setShowModal(true);
      })
      .catch((error) => {
        console.error("Error deleting category:", error);
        setModalMessage("Error deleting category: " + error.message);
        setModalType("error");
        setShowModal(true);
      });
  };

  const cancelDelete = () => setShowDeleteModal(false);

  // Filter categories based on search input.
  const filteredCategories = categories.filter((category) =>
    category?.category_name?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-white shadow-md">
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          <FaBars className="text-2xl text-gray-600" />
        </button>
        <span className="text-xl font-bold text-gray-700">Categories</span>
      </div>
      <div className="flex flex-col md:flex-row min-h-screen overflow-hidden">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? "block" : "hidden"} md:block w-full md:w-64 bg-white shadow-md flex flex-col`}>
          <div className="hidden md:flex items-center justify-center py-6">
            <div className="text-3xl font-bold text-gray-600 flex items-center">
              <FaChartPie style={{ fontSize: "48px", marginRight: "10px" }} />
              <span className="text-2xl md:text-3xl font-bold" style={{ color: "gray-600" }}>
                Dashboard
              </span>
            </div>
          </div>
          {/* Navigation */}
          <nav className="mt-6 flex flex-col flex-grow text-gray-600">
            <ul>
              {[
                { name: "Home", icon: <FaHome className="mr-2" style={{ fontSize: "20px" }} />, link: "/Dashboard" },
                { name: "Categories", icon: <FaList className="mr-2" style={{ fontSize: "20px" }} />, link: "/Categories" },
                { name: "Subcategories", icon: <FaTh className="mr-2" style={{ fontSize: "20px" }} />, link: "/Subcategories" },
                { name: "Products", icon: <FaBox className="mr-2" style={{ fontSize: "20px" }} />, link: "/Products" },
                { name: "Attributes", icon: <FaTags className="mr-2" style={{ fontSize: "20px" }} />, link: "/Attributes" },
              ].map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => navigate(item.link)}
                  className={`cursor-pointer px-6 py-2 rounded-lg flex items-center justify-between text-lg ${
                    item.name === "Categories" ? "bg-gray-200 scale-90" : "hover:bg-gray-200 hover:scale-90"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                  <FaChevronRight className="text-gray-600" />
                </li>
              ))}
            </ul>
          </nav>
          {/* Sign Out */}
          <div className="mt-auto flex flex-col text-red-600">
            <ul>
              {[
                { name: "Signout", icon: <FaSignOutAlt className="mr-2" style={{ fontSize: "20px" }} /> },
              ].map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => navigate("/Logout")}
                  className="cursor-pointer px-6 py-2 hover:bg-gray-200 rounded-lg flex items-center justify-between text-lg"
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          <div className="mb-4">
            <div className="flex items-center mb-4">
              <FaList className="text-gray-700 mr-2 text-2xl" />
              <span className="text-2xl font-bold text-gray-700">Categories</span>
            </div>
            {/* Add Category Button & Search Bar */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <button onClick={() => setShowAddCard(true)} className="bg-gray-500 text-white py-2 px-6 rounded hover:bg-gray-600 whitespace-nowrap">
                Add Category
              </button>
              <div className="w-full md:w-64">
                <input
                  placeholder="Search..."
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border border-gray-400 focus:ring-gray-500 rounded p-2 w-full"
                />
              </div>
            </div>
          </div>

          {/* Global Modal for messages */}
          {showModal && (
            <div className="fixed inset-0 z-[1000] bg-gray-400 bg-opacity-50 flex items-center justify-center" onClick={() => setShowModal(false)}>
              <div className="bg-white p-6 rounded shadow-md w-80" onClick={(e) => e.stopPropagation()}>
                <p className={`text-center ${modalType === "success" ? "text-black" : "text-red-500"}`}>{modalMessage}</p>
                <div className="mt-4 text-center">
                  <button onClick={() => setShowModal(false)} className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">
                    {modalType === "success" ? "Done" : "Close"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
                <p>Are you sure you want to delete "{categoryToDelete?.category_name}"?</p>
                <div className="flex justify-end gap-4 mt-4">
                  <button onClick={confirmDeleteCategory} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                    Delete
                  </button>
                  <button onClick={cancelDelete} className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add/Edit Category Modal */}
          {showAddCard && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
              <div className="bg-white p-6 rounded shadow-md w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-xl font-semibold mb-4">{editingId !== null ? "Edit Category" : "Add Category"}</h2>
                <input
                  placeholder="Name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="border border-gray-400 rounded p-2 w-full mb-4"
                />
                {/* Image Input Field */}
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="image">
                    Category Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    id="image"
                    onChange={handleImageChange}
                    className="border border-gray-400 rounded p-2 w-full mb-2"
                  />
                  {imagePreview && (
                    <div className="relative w-12 h-12">
                      <img src={imagePreview} alt="Selected" className="object-cover w-full h-full rounded" />
                      <button type="button" onClick={removeImage} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600">
                        <FaTimes className="text-sm" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-4">
                  <button onClick={() => setShowAddCard(false)} className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
                    Cancel
                  </button>
                  <button onClick={editingId !== null ? handleUpdate : handleAdd} className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">
                    {editingId !== null ? "Update" : "Add"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Categories Table */}
          <div className="overflow-x-auto">
            <table className="table-auto w-full border-collapse border border-gray-400">
              <thead>
                <tr>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">ID</th>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">Category</th>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">Image</th>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((category) => (
                  <tr key={category.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 md:px-10 py-2 text-gray-700">{category.id}</td>
                    <td className="px-4 md:px-10 py-2 text-gray-700">{category.category_name}</td>
                    <td className="px-4 md:px-10 py-2 text-gray-700">
                      {category.image ? (
                        <img
                          src={`${API_URL}/storage/${category.image}`}
                          alt={category.category_name}
                          className="w-16 h-12 object-cover cursor-pointer rounded"
                          onClick={() => {
                            setModalImageUrl(`${API_URL}/storage/${category.image}`);
                            setShowImageModal(true);
                          }}
                        />
                      ) : (
                        <FaImage className="text-gray-400 text-6xl" />
                      )}
                    </td>
                    <td className="px-4 md:px-10 py-2">
                      <button onClick={() => handleEdit(category)} className="bg-blue-500 text-white py-1 px-3 rounded hover:bg-blue-600 mr-2">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(category)} className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="mt-4 flex justify-center items-center gap-4">
            <button className={`px-4 py-2 rounded-md ${
                currentPage === 1 ? "bg-gray-300 cursor-not-allowed" : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              Previous
            </button>
            <span className="text-black">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button className={`px-4 py-2 rounded-md ${
                currentPage === totalPages || totalPages === 0 ? "bg-gray-300 cursor-not-allowed" : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            >
              Next
            </button>
          </div>
        </main>
      </div>

      {/* Full-size Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowImageModal(false)}>
          <div className="bg-white p-4 rounded" onClick={(e) => e.stopPropagation()}>
            <img src={modalImageUrl} alt="Category Full" className="max-w-full max-h-[80vh]" />
          </div>
        </div>
      )}
    </>
  );
};

export default Categories;
